import { useCallback, useEffect, useRef, useState, type ComponentRef } from 'react';
import {
  fetchUserAudit,
  StaffError,
  userAction,
  type AuditReportRow,
  type UserActionName,
  type UserAudit,
} from '../lib/staffClient';
import { Attachments } from './Attachments';
import { Icon } from './Icon';

type Tab = 'activity' | 'against' | 'filed' | 'history';

const TAB_LABEL: Record<Tab, string> = {
  activity: 'Activity',
  against: 'Reports against',
  filed: 'Reports filed',
  history: 'Staff history',
};

const when = (iso: string) => (iso ? iso.slice(0, 16).replace('T', ' ') : '');

function ReportList({ rows, empty }: { rows: AuditReportRow[]; empty: string }) {
  if (rows.length === 0) return <p className="skf-meta">{empty}</p>;
  return (
    <ul className="audit-list">
      {rows.map((r) => (
        <li key={r.id}>
          <p className="audit-line">
            <strong>{r.reason}</strong> · {r.status} · {when(r.created)}
            {r.skiff_title && <> · {r.skiff_title}</>}
          </p>
          <p className="audit-line audit-muted">
            {r.reporter} reported {r.reported_user}
          </p>
          {r.content_snippet && (
            <blockquote className="staff-quote">{r.content_snippet}</blockquote>
          )}
          <Attachments items={r.attachments} hidden={r.reason === 'illegal_content'} />
        </li>
      ))}
    </ul>
  );
}

/** Staff audit of one user: activity, reports both ways, and account actions. */
export function UserAuditModal({
  userId,
  initialTab,
  onClose,
  onUnauthorized,
}: {
  userId: string;
  initialTab: Tab;
  onClose: () => void;
  onUnauthorized: () => void;
}) {
  const ref = useRef<ComponentRef<'dialog'>>(null);
  const [data, setData] = useState<UserAudit | null>(null);
  const [tab, setTab] = useState<Tab>(initialTab);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const fail = useCallback(
    (err: unknown) => {
      if (err instanceof StaffError && err.status === 401) onUnauthorized();
      else setError(err instanceof StaffError ? err.message : 'Something went wrong.');
    },
    [onUnauthorized],
  );

  const load = useCallback(async () => {
    try {
      setData(await fetchUserAudit(userId));
    } catch (err) {
      fail(err);
    }
  }, [userId, fail]);

  useEffect(() => {
    ref.current?.showModal();
    void load();
  }, [load]);

  async function run(action: UserActionName, label: string, skiffId?: string) {
    if (reason.trim().length < 3) {
      setError('Add a reason first — it is recorded in the staff log.');
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await userAction(userId, action, reason.trim(), skiffId);
      setNotice(`${label} done.`);
      setConfirmDelete(false);
      await load();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  }

  const u = data?.user;
  const banned = u?.account_status === 'banned';
  const illegal = data?.reports_against.some((r) => r.reason === 'illegal_content');
  const stats = data?.filing_stats;

  return (
    <dialog ref={ref} className="audit-dialog" onClose={onClose}>
      <header className="audit-head">
        <div>
          <h2 className="staff-h2">{u ? u.display_name || u.id : 'Loading…'}</h2>
          {u && (
            <p className="skf-meta">
              {u.email} · joined {when(u.created).slice(0, 10)} ·{' '}
              <span className={banned ? 'staff-flag' : undefined}>{u.account_status}</span>
            </p>
          )}
        </div>
        <button
          className="btn btn--ghost btn--icon"
          aria-label="Close"
          title="Close"
          onClick={() => ref.current?.close()}
        >
          <Icon name="x-lg" />
        </button>
      </header>

      {illegal && (
        <div className="audit-alert" role="note">
          <strong>Reported as illegal content.</strong> Do not delete anything — preserve it.
          Apparent child sexual abuse material must be reported to the NCMEC CyberTipline
          (report.cybertip.org); imminent danger goes to local emergency services first. Filing
          stays a manual step and the details need counsel — follow §5 of{' '}
          <code>docs/runbooks/moderation-response.md</code>.
        </div>
      )}

      {error && (
        <p className="staff-error" role="alert">
          {error}
        </p>
      )}
      {notice && <p className="skf-meta">{notice}</p>}

      {data && u && (
        <>
          <div className="audit-tabs" role="tablist">
            {(Object.keys(TAB_LABEL) as Tab[]).map((t) => (
              <button
                key={t}
                role="tab"
                aria-selected={t === tab}
                className={`staff-tab${t === tab ? ' staff-tab--on' : ''}`}
                onClick={() => setTab(t)}
              >
                {TAB_LABEL[t]}
                {t === 'against' && ` (${data.reports_against.length})`}
                {t === 'filed' && ` (${stats?.total ?? 0})`}
              </button>
            ))}
          </div>

          <div className="audit-body">
            {tab === 'activity' && (
              <>
                <h3 className="staff-h3">Skiffs</h3>
                {data.memberships.length === 0 && <p className="skf-meta">No memberships.</p>}
                <ul className="audit-list">
                  {data.memberships.map((m) => (
                    <li key={m.skiff_id + m.joined_at} className="audit-row">
                      <span>
                        {m.skiff_title || m.skiff_id} · {m.role} · {m.status}
                      </span>
                      {m.status === 'active' && m.role !== 'captain' && !u.is_admin && (
                        <button
                          className="btn btn--ghost"
                          disabled={busy}
                          onClick={() => void run('remove_from_skiff', 'Removal', m.skiff_id)}
                        >
                          <Icon name="box-arrow-left" /> Remove
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
                <h3 className="staff-h3">Recent messages</h3>
                {data.recent_messages.length === 0 && <p className="skf-meta">None.</p>}
                <ul className="audit-list">
                  {data.recent_messages.map((m) => (
                    <li key={m.id}>
                      <p className="audit-line audit-muted">
                        {when(m.created)} · {m.skiff_title}
                        {m.removed && ' · removed'}
                      </p>
                      {m.content && <p className="audit-line">{m.content}</p>}
                      <Attachments items={m.attachments} />
                    </li>
                  ))}
                </ul>
                <h3 className="staff-h3">Recent check-ins</h3>
                {data.recent_checkins.length === 0 && <p className="skf-meta">None.</p>}
                <ul className="audit-list">
                  {data.recent_checkins.map((c) => (
                    <li key={c.id}>
                      <p className="audit-line audit-muted">
                        {when(c.created)} · {c.skiff_title}
                      </p>
                      {c.note && <p className="audit-line">{c.note}</p>}
                      <Attachments items={c.attachments} />
                    </li>
                  ))}
                </ul>
              </>
            )}
            {tab === 'against' && (
              <ReportList rows={data.reports_against} empty="Nobody has reported this user." />
            )}
            {tab === 'filed' && stats && (
              <>
                <p className="audit-line">
                  {stats.total} filed · {stats.last_30d} in the last 30 days · {stats.dismissed}{' '}
                  dismissed · {stats.resolved} resolved · {stats.pending} pending
                  {stats.dismissal_rate !== null &&
                    ` · ${Math.round(stats.dismissal_rate * 100)}% of decided reports dismissed`}
                </p>
                {stats.top_reported.length > 0 && (
                  <p className="audit-line audit-muted">
                    Most reported:{' '}
                    {stats.top_reported.map((t) => `${t.name} (${t.count})`).join(', ')}
                  </p>
                )}
                <ReportList rows={data.reports_filed} empty="This user has not filed reports." />
              </>
            )}
            {tab === 'history' && (
              <ul className="audit-list">
                {data.moderation_history.length === 0 && (
                  <li className="skf-meta">No staff actions on this user.</li>
                )}
                {data.moderation_history.map((h, i) => (
                  <li key={i}>
                    <p className="audit-line">
                      <strong>{h.action.replace(/_/g, ' ')}</strong> · {when(h.created)} · {h.actor}
                    </p>
                    {h.reason && <p className="audit-line audit-muted">{h.reason}</p>}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {!u.is_admin && (
            <footer className="audit-actions">
              <input
                className="staff-input"
                aria-label="Reason (recorded in the staff log)"
                placeholder="Reason — required, recorded in the staff log"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              <div className="staff-actions">
                <button
                  className="btn btn--ghost"
                  disabled={busy}
                  onClick={() => void run('warn', 'Warning')}
                >
                  <Icon name="exclamation-triangle" /> Warn
                </button>
                {banned ? (
                  <button
                    className="btn btn--ghost"
                    disabled={busy}
                    onClick={() => void run('unban', 'Unban')}
                  >
                    <Icon name="unlock" /> Unban
                  </button>
                ) : (
                  <button
                    className="btn btn--danger"
                    disabled={busy}
                    onClick={() => void run('ban', 'Ban')}
                  >
                    <Icon name="slash-circle" /> Ban
                  </button>
                )}
                <button
                  className="btn btn--ghost"
                  disabled={busy || stats?.pending === 0}
                  title="Dismiss this user's pending reports (never illegal-content ones)"
                  onClick={() => void run('dismiss_reporter_reports', 'Dismissal')}
                >
                  <Icon name="x-circle" /> Dismiss their reports
                </button>
                {confirmDelete ? (
                  <>
                    <button
                      className="btn btn--danger"
                      disabled={busy}
                      onClick={() => void run('delete_account', 'Deletion request')}
                    >
                      <Icon name="trash" /> Confirm: start deletion
                    </button>
                    <button className="btn btn--ghost" onClick={() => setConfirmDelete(false)}>
                      Never mind
                    </button>
                  </>
                ) : (
                  <button
                    className="btn btn--ghost"
                    disabled={busy}
                    onClick={() => setConfirmDelete(true)}
                  >
                    <Icon name="trash" /> Delete account…
                  </button>
                )}
              </div>
            </footer>
          )}
        </>
      )}
    </dialog>
  );
}
